import { useMutation } from '@tanstack/react-query';
import { VehicleLookupService } from "@/services/vehicleLookup/vehicleLookupService";
import { convertAutoUncleToVehicleDetails } from "@/selectors/autoUncleSelectors";
import type { AutoUncleCarInfoLookupResponse } from "@/types/autoUncle";
import type { VehicleDetails } from "@/types/vehicleDetails";

interface GetVehicleInformationParams {
  lpn?: string;
  vin?: string;
}

interface VehicleLookupResult {
  vehicleDetails: Partial<VehicleDetails>;
  rawResponse: AutoUncleCarInfoLookupResponse;
}

export const useVehicleLookup = () => {
  /**
   * Get vehicle information mutation
   * This is a mutation because it's triggered manually by user action
   */
  const getVehicleInformationMutation = useMutation({
    mutationFn: async ({ lpn, vin }: GetVehicleInformationParams): Promise<VehicleLookupResult> => {
      const response = await VehicleLookupService.getVehicleInformation(lpn, vin);

      // Convert AutoUncle format to VehicleDetails using selector
      const vehicleDetails = convertAutoUncleToVehicleDetails(response.car);
      return {
        vehicleDetails,
        rawResponse: response,
      };
    },
  });

  return {
    getVehicleInformationMutation,
    getVehicleInformationAsync: getVehicleInformationMutation.mutateAsync,
    isLoading: getVehicleInformationMutation.isPending,
    error: getVehicleInformationMutation.error,
    data: getVehicleInformationMutation.data?.vehicleDetails ?? null,
    rawData: getVehicleInformationMutation.data?.rawResponse ?? null,
  };
};
